import React, { useEffect } from 'react'

import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Switch,
} from 'antd'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import axios from 'axios'
import { getInstitutionSchedules } from '@app/store/actions/institution-schedules-actions'
import { LOADING } from '@app/constants/redux-state'
import locale from 'antd/es/date-picker/locale/ru_RU'
import { IOrganizationLocality } from '@app/interfaces/organization/organization-locality'
import useLocalization from '@app/hooks/useLocalization'
import { getOrganizationLocalities } from '@app/store/actions/organization-locality-actions'
import { useGetOrganizationSchedulesQuery } from '@app/store/rtk/api/organization/organization-schedule-api'
import { IReferenceBillingTariff } from '@app/interfaces/reference-billing'
import NumberInput from '../number-input/number-input'
import { useUpdateOneTariffDataMutation } from '@app/store/rtk/api/billing-tariffs-api'
import { openNotificationWithIcon } from '@app/shared/notification/notification'

type Props = {
  visible: boolean
  onCancel: () => void
  tariff: IReferenceBillingTariff
}

export const UpdateBillingTariffModal = (props: Props) => {
  const { t } = useTranslation()
  const { localizeData } = useLocalization()
  const [form] = Form.useForm()
  const { data: institutionSchedules } = useGetOrganizationSchedulesQuery('')

  const organizationLocalities = useSelector(
    (v: any) =>
      v.organizationLocality.organizationLocalities as IOrganizationLocality[],
  )

  const state = useSelector((v: any) => v.institutionSchedules.state as string)
  const dispatch = useDispatch()

  const onSuccess = () => {
    // dispatch(
    //   optimisticUpdateReferenceBillingTariffsState('add', {
    //     fullName: form.getFieldValue('fullName'),
    //     username: form.getFieldValue('username'),
    //     id: newUserId,
    //   }),
    // )
  }

  const [updateBillingTariff, { isLoading: isUpdating }] =
    useUpdateOneTariffDataMutation()
  const handleSubmit = async (values) => {
    try {
      await updateBillingTariff({
        id: props.tariff.id,

        organizationLocalityId: values.location,
        orderNo: values.order_number,
        orderDate: values.order_date.format('YYYY-MM-DD'),
        organizationScheduleId: values.workTimes?.[0]?.id,
        value: +values.workTimes?.[0]?.one_child * 100,
        discountedValue: +values.workTimes?.[0]?.two_or_more_child * 100,
      }).unwrap()

      onSuccess()
      form.resetFields()
      props.onCancel()
    } catch (error) {
      openNotificationWithIcon('error', t('billing.fail_update_tariff'))
    }
  }

  useEffect(() => {
    const source = axios.CancelToken.source()
    if (isEmpty(institutionSchedules))
      dispatch(
        getInstitutionSchedules({ source }, t('institutionSchedule.error')),
      )
    return () => {
      source.cancel('Component got unmounted')
    }
  }, [])
  useEffect(() => {
    const source = axios.CancelToken.source()
    if (isEmpty(organizationLocalities))
      dispatch(
        getOrganizationLocalities({ source }, t('organizationLocality.error')),
      )
    return function cleanup() {
      source.cancel('Component got unmounted')
    }
  }, [])

  useEffect(() => {
    const tariff = props.tariff
    if (tariff) {
      form.setFieldsValue({
        status: tariff.status,
        location: tariff.organizationLocality.id,
        workTimes: [
          {
            id: tariff.id,
            time:
              localizeData(tariff.organizationSchedule.data)?.name ??
              tariff.organizationSchedule.data?.[0]?.name ??
              '',
            one_child: tariff.value / 100,
            two_or_more_child: tariff.discountedValue / 100,
          },
        ],
      })
    }
  }, [props.tariff])

  return (
    <Modal
      visible={props.visible}
      title={t('billing.change_tariff')}
      onCancel={() => {
        props.onCancel()
        form.resetFields()
      }}
      footer={null}
      width={660}>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          status: false,
        }}>
        <Form.Item
          name="status"
          label={t('billing.status')}
          valuePropName="checked">
          <Switch
            checkedChildren={t('general.active')}
            unCheckedChildren={t('general.inActive')}
          />
        </Form.Item>
        <Form.Item
          name="location"
          label={t('billing.organization_location')}
          rules={[{ required: true, message: t('errors.requiredMessage') }]}>
          <Select disabled>
            {organizationLocalities.map((item) => {
              return (
                <Select.Option value={item.id} key={item.id}>
                  {localizeData(item.data)?.name}
                </Select.Option>
              )
            })}
          </Select>
        </Form.Item>

        <Row gutter={10}>
          <Col span={6}>{t('billing.work_time')}</Col>
          <Col span={9}>{t('billing.one_child_sum')}</Col>
          <Col span={9}>{t('billing.two_children_sum')}</Col>
        </Row>
        {state === LOADING ? (
          'Loading...'
        ) : (
          <Form.List
            name={'workTimes'}
            rules={[
              {
                validator(rule, value, callback) {
                  if (value.length === 0) {
                    callback(t('errors.requiredMessage'))
                  } else {
                    callback()
                  }
                },
              },
            ]}>
            {(fields, { add, remove }) => (
              <Row gutter={10}>
                {fields.map((field, index) => (
                  <React.Fragment key={field.key}>
                    <Col span={6}>
                      <Form.Item
                        name={[index, 'time']}
                        rules={[{ required: true }]}
                        style={{
                          pointerEvents: 'none',
                        }}>
                        <Input tabIndex={-1} placeholder="" readOnly />
                      </Form.Item>
                    </Col>
                    <Col span={9}>
                      <Form.Item
                        name={[index, 'one_child']}
                        rules={[{ required: true }]}>
                        <NumberInput />
                      </Form.Item>
                    </Col>
                    <Col span={9}>
                      <Form.Item
                        name={[index, 'two_or_more_child']}
                        rules={[{ required: true }]}>
                        <NumberInput />
                      </Form.Item>
                    </Col>
                  </React.Fragment>
                ))}
              </Row>
            )}
          </Form.List>
        )}

        <Row gutter={20} justify={'center'}>
          <Col span={24}>
            <p style={{ textAlign: 'center' }}>
              <strong>{t('billing.order_number_and_date')}</strong>
            </p>
          </Col>
          <Col span={9}>
            <Form.Item name={'order_number'} rules={[{ required: true }]}>
              <Input placeholder={t('billing.order_number')} />
            </Form.Item>
          </Col>
          <Col span={9}>
            <Form.Item name={'order_date'} rules={[{ required: true }]}>
              <DatePicker
                format={'DD-MM-YYYY'}
                style={{ width: '100%' }}
                locale={locale}
                placeholder={t('billing.order_date')}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item className="text-right">
          <Button onClick={props.onCancel} style={{ marginRight: 8 }}>
            {t('general.cancel')}
          </Button>
          <Button type="primary" htmlType="submit" loading={isUpdating}>
            {t('general.save')}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  )
}
